import { useImportReapproNeedModificationsBulk } from '@gozoki/api'
import type { RowError } from '@gozoki/api-types'
import { AxiosError } from 'axios'
import { Loader2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'

interface ReapproNeedBulkImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface ImportResult {
  updated: number
  unchanged: number
  rejected: number
  errors: RowError[]
}

export const ReapproNeedBulkImportDialog = ({
  open,
  onOpenChange,
}: ReapproNeedBulkImportDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [importError, setImportError] = useState<string | null>(null)

  const { mutate: importXlsx, isPending } = useImportReapproNeedModificationsBulk()

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      setSelectedFile(null)
      setImportResult(null)
      setImportError(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleImport = () => {
    if (!selectedFile) return

    setImportResult(null)
    setImportError(null)

    importXlsx(selectedFile, {
      onSuccess: (data) => {
        setImportResult(data)
        setSelectedFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
      },
      onError: (error) => {
        if (error instanceof AxiosError && error.response?.data) {
          const data = error.response.data
          setImportError(data.message ?? "Erreur lors de l'import")
        } else {
          setImportError("Une erreur inconnue est survenue lors de l'import")
        }
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Import modifications besoins de réappro / dispatch</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Ré-importez le fichier XLSX de simulation reçu par email avec les quantités corrigées.
            Seuls les besoins dont la catégorie est dans sa fenêtre de modification (entre l'heure
            de calcul et l'heure d'envoi) seront pris en compte.
          </p>

          <div className="space-y-2">
            <label className="text-xs text-muted-foreground uppercase tracking-wider">
              Fichier XLSX
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={(e) => {
                setSelectedFile(e.target.files?.[0] ?? null)
                setImportResult(null)
                setImportError(null)
              }}
              className="w-full text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-muted/80"
            />
          </div>

          {importError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {importError}
            </div>
          )}

          {importResult && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 space-y-1">
              <p className="text-sm font-medium text-green-800">Import terminé</p>
              <ul className="text-sm text-green-700 list-disc list-inside">
                <li>{importResult.updated} besoin(s) modifié(s)</li>
                <li>{importResult.unchanged} besoin(s) inchangé(s)</li>
                {importResult.rejected > 0 && (
                  <li className="text-amber-600">
                    {importResult.rejected} besoin(s) refusé(s) (hors fenêtre de modification)
                  </li>
                )}
                {importResult.errors.length > 0 && (
                  <li className="text-red-600">{importResult.errors.length} erreur(s)</li>
                )}
              </ul>
              {importResult.errors.length > 0 && (
                <ul className="mt-2 text-xs text-red-600 space-y-0.5">
                  {importResult.errors.map((err, i) => (
                    <li key={i}>
                      {err.reference} : {err.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Fermer</DialogClose>
          <Button onClick={handleImport} disabled={!selectedFile || isPending}>
            {isPending ? <Loader2 className="animate-spin" /> : <Upload />}
            Importer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
